import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { help } from '../helpTexts';
import { formatFileSize } from '../helpers';
import { LAUNCH_FILE_ACCEPT, LAUNCH_FILE_EXTENSIONS, launchFileError, MAX_LAUNCH_FILES_PER_UPLOAD } from '../fileRules';
import { LaunchFileRules } from '../types';
import { launchColors } from '../launchTheme';
import InfoTip from './InfoTip';

interface FolderOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  folders: FolderOption[];
  defaultFolderId?: string;
  saving: boolean;
  progress: number | null;
  error: string | null;
  rules?: LaunchFileRules;
  onClose: () => void;
  onSubmit: (files: File[], folderId: string) => void;
}

const preferredFolder = (options: FolderOption[], preferred?: string): string => (
  options.find((folder) => folder.id === preferred)?.id
  || options.find((folder) => folder.id === 'folder-ovrigt')?.id
  || options[0]?.id
  || ''
);

const UploadFilesDialog: React.FC<Props> = ({
  open, folders, defaultFolderId, saving, progress, error, rules, onClose, onSubmit,
}) => {
  const accept = (rules?.allowedExtensions ?? LAUNCH_FILE_EXTENSIONS).map((extension) => `.${extension}`).join(',') || LAUNCH_FILE_ACCEPT;
  const [folderId, setFolderId] = useState(() => preferredFolder(folders, defaultFolderId));
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFolderId(preferredFolder(folders, defaultFolderId));
    setFiles([]);
    setLocalError(null);
    setDragOver(false);
    // Återställ bara när dialogen öppnas, inte när föräldern renderar om.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFiles = (list: FileList | File[] | null) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const invalid = incoming.map((file) => ({ file, message: launchFileError(file, rules) })).find((item) => item.message);
    if (invalid?.message) {
      setLocalError(`${invalid.file.name}: ${invalid.message}`);
      return;
    }
    if (files.length + incoming.length > MAX_LAUNCH_FILES_PER_UPLOAD) {
      setLocalError('Högst 10 filer kan laddas upp åt gången.');
      return;
    }
    setLocalError(null);
    setFiles((current) => [...current, ...incoming]);
  };

  const message = localError || error;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
        Bifoga filer
        <InfoTip text={help.filesUpload} />
        <Box sx={{ flex: 1 }} />
        <IconButton aria-label="Stäng" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            select
            label="Mapp"
            value={folderId}
            onChange={(event) => setFolderId(event.target.value)}
            disabled={saving || folders.length === 0}
            helperText={folders.length === 0 ? 'Skapa en mapp först.' : 'Filen syns för alla som kan se lanseringen.'}
          >
            {folders.map((folder) => <MenuItem key={folder.id} value={folder.id}>{folder.name}</MenuItem>)}
          </TextField>
          <Box
            aria-label="Släpp filer här"
            onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              addFiles(event.dataTransfer.files);
            }}
            sx={{
              border: `1px dashed ${dragOver ? launchColors.navy : launchColors.divider}`,
              bgcolor: dragOver ? launchColors.softNavy : launchColors.section,
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: 14, mb: 1 }}>Dra hit filer eller välj från datorn.</Typography>
            <Button component="label" variant="outlined" disabled={saving}>
              Välj filer
              <input
                type="file"
                multiple
                accept={accept}
                aria-label="Välj filer"
                style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </Button>
          </Box>
          {files.length > 0 && (
            <Stack spacing={0.5}>
              {files.map((file, index) => (
                <Stack key={`${file.name}-${file.size}-${index}`} direction="row" alignItems="center" spacing={1}>
                  <Typography noWrap sx={{ flex: 1, fontSize: 14 }}>{file.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{formatFileSize(file.size)}</Typography>
                  <IconButton aria-label={`Ta bort ${file.name} från listan`} size="small" disabled={saving} onClick={() => setFiles((current) => current.filter((_, item) => item !== index))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
          {saving && <LinearProgress variant={progress == null ? 'indeterminate' : 'determinate'} value={progress ?? 0} aria-label="Uppladdning pågår" />}
          {message && <Alert severity="error">{message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Avbryt</Button>
        <Button variant="contained" disabled={saving || !files.length || !folderId} onClick={() => onSubmit(files, folderId)}>
          {saving ? 'Laddar upp…' : 'Ladda upp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadFilesDialog;
